import { type Item } from '@cheqroom/api';

import { getCheckoutQuantities } from './get-checkout-quantities';

type GetStatusTextOptions = {
	isUnavailable: boolean;
	canSeeUsers: boolean;
};

type StatusText =
	| {
			key: string;
			values?: Record<string, string | number>;
	  }
	| {
			key: 'assigned_with_link';
			values: {
				inCustodyOf?: string;
			};
	  };

type GetStatusTextItem = Pick<
	Item,
	'status' | 'kind' | 'inKits' | 'quantity' | 'kitQuantities' | 'orderQuantitiesWithKits'
> & {
	kit?: Pick<NonNullable<Item['kit']>, '_id'>;
	custody?: Pick<NonNullable<Item['custody']>, 'name'>;
};

export const getStatusText = (item: GetStatusTextItem, options: GetStatusTextOptions): StatusText => {
	const { isUnavailable, canSeeUsers } = options;

	if (isUnavailable) {
		return { key: 'unavailable' };
	}

	if (item.kind === 'bulk') {
		if (item.status === 'expired') return { key: 'retired' };

		const looseItemQuantity = item.quantity - Number(item.inKits ?? 0);
		const {
			kitQuantity: looseKitQuantity,
			checkedOutKitQuantity: checkedOutLooseKitQuantity,
			checkedOutLooseQuantity,
		} = getCheckoutQuantities(item);

		if (looseKitQuantity > 0 && looseKitQuantity == checkedOutLooseKitQuantity) {
			return {
				key: 'bulk_in_checkout',
				values: { checkedOutKitQuantity: checkedOutLooseKitQuantity, kitQuantity: looseKitQuantity },
			};
		}

		return {
			key: 'bulk',
			values: {
				availableQuantity: Math.max(
					0,
					looseKitQuantity
						? looseKitQuantity - checkedOutLooseKitQuantity
						: looseItemQuantity - Math.max(0, checkedOutLooseQuantity)
				),
				totalQuantity: looseKitQuantity ? looseKitQuantity : item.quantity,
			},
		};
	} else if (item.status === 'in_custody') {
		if (!canSeeUsers) return { key: 'assigned' };

		return {
			key: 'assigned_with_link',
			values: {
				inCustodyOf: item.custody?.name,
			},
		};
	} else if (item.status === 'checkedout') {
		return { key: 'checked_out' };
	} else if (item.status === 'await_checkout') {
		return { key: 'checking_out' };
	}

	return { key: item.status.toLowerCase() };
};
